import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import "./AreaGraphTwoLine.css";
import { Box } from "@mui/material";
import { grey } from "@mui/material/colors";

const AreaGraphTwoLine = ({ column = {}, data = {}, width = 500, height, label }) => {
	const { x, y, l1, l2 } = column;
	const margin = { top: 10, right: 10, bottom: 10, left: 30 };
	const xAxislabelSize = 8;
	const calculatedWidth = width - margin.left - margin.right;
	const heightBasedOnWidth = width - Math.round(width / 2);
	const calculatedHeight = (height ? height : heightBasedOnWidth) - margin.top - margin.bottom;

	const svgRef = useRef();
	const tooltipRef = useRef();

	useEffect(() => {
		const svg = d3.select(svgRef.current);
		const tooltip = d3.select(tooltipRef.current);

		svg.selectAll("*").remove(); // Clear previous render
		const chart = svg.attr("viewBox", `0 0 ${calculatedWidth} ${calculatedHeight + xAxislabelSize}`).append("g");
		// .attr("transform", `translate(${margin.left},${margin.top})`);

		const xScale = d3
			.scalePoint()
			.domain(data.map((d) => d[x.dataCol]))
			.range([margin.left, calculatedWidth])
			.padding(0.8);
		const yScale = d3
			.scaleLinear()
			.domain([0, d3.max(data, (d) => Math.max(d[l1.dataCol] ?? 0, d[l2.dataCol] ?? 0)) * 1.2])
			.nice()
			.range([calculatedHeight - xAxislabelSize, margin.bottom]);

		// Axes
		// chart.append("g").attr("transform", `translate(0, ${height})`).call(d3.axisBottom(x));
		chart
			.append("g")
			.attr("transform", `translate(0, ${calculatedHeight - margin.top - margin.bottom + xAxislabelSize})`)
			.call(d3.axisBottom(xScale))
			.call((g) => {
				g.select(".domain").remove(); // remove bottom axis line
				g.selectAll(".tick line").remove(); // remove vertical tick lines (|)
				g.selectAll(".tick text") // increase font size
					.style("font-size", "12px")
					.style("fill", "#000") // optional: soften color
					.style("text-transform", "uppercase");
			}); // chart.append("g").call(d3.axisLeft(y));
		svg.append("foreignObject")
			.attr("x", 0)
			.attr("y", 0)
			.attr("width", margin.left)
			.attr("height", height ?? heightBasedOnWidth)
			.append("xhtml:div")
			.style("display", "flex")
			.style("align-items", "center")
			.style("justify-content", "center")
			.style("height", "100%")
			.style("background", "black")
			.style("color", "white")
			.style("padding", "4px")
			.style("writing-mode", "sideways-lr")
			.style("text-orientation", "mixed")
			.style("font-size", "15")
			.text(y.label ?? y.dataCol);

		// Line generators
		const lineTY = d3
			.line()
			.x((d) => xScale(d[x.dataCol]))
			.y((d) => yScale(d[l2.dataCol]));

		const lineLY = d3
			.line()
			.x((d) => xScale(d[x.dataCol]))
			.y((d) => yScale(d[l1.dataCol]));
		const areaLY = d3
			.area()
			.x((d) => xScale(d[x.dataCol]))
			.y0(() => yScale(0)) // bottom
			.y1((d) => yScale(d[l1.dataCol] ?? 0));
		// .curve(d3.curveMonotoneX);

		const areaTY = d3
			.area()
			.x((d) => xScale(d[x.dataCol]))
			.y0(() => yScale(0)) // bottom
			.y1((d) => yScale(d[l2.dataCol] ?? 0));
		// .curve(d3.curveMonotoneX);
		// Draw lines
		chart.append("path").datum(data).attr("d", lineLY).attr("fill", "none").attr("stroke", l1.color).attr("stroke-width", 1.2);
		chart.append("path").datum(data).attr("d", lineTY).attr("fill", "none").attr("stroke", l2.color).attr("stroke-width", 1.2);

		// Area lines
		chart.append("path").datum(data).attr("d", areaLY).attr("fill", l1.color);
		// .attr("opacity", 0.5);
		chart.append("path").datum(data).attr("d", areaTY).attr("fill", l2.color);
		// .attr("opacity", 0.5);
		// Add Hover Elements
		const focusLine = chart
			.append("line")
			.style("stroke", grey[300])
			// .style("stroke-", "3 3")
			.style("opacity", 0)
			.attr("y1", 0)
			.attr("y2", calculatedHeight);
		// chart.append("path").datum(data).attr("d", lineLY).attr("fill", "none").attr("stroke", l1.color).attr("stroke-width", 1.2);

		// chart.append("path").datum(data).attr("d", lineTY).attr("fill", "none").attr("stroke", l2.color).attr("stroke-width", 1.2);

		const focusDotTY = chart.append("circle").attr("r", 3).attr("fill", l2.color).style("opacity", 0);

		const focusDotLY = chart.append("circle").attr("r", 3).attr("fill", l1.color).style("opacity", 0);

		// Add overlay for hover
		chart
			.append("rect")
			.attr("width", calculatedWidth)
			.attr("height", calculatedHeight - margin.bottom + xAxislabelSize)
			.style("fill", "none")
			.style("pointer-events", "all")
			.on("mousemove", function (event) {
				const [xm] = d3.pointer(event);
				const i = d3.bisector((d) => xScale(d[x.dataCol])).center(data, xm);
				const d = data[i];

				if (!d) return;

				const xCoord = xScale(d[x.dataCol]);
				const yTY = yScale(d[l2.dataCol]);
				const yLY = yScale(d[l1.dataCol]);

				focusLine.attr("x1", xCoord).attr("x2", xCoord).style("opacity", 1);
				focusDotTY.attr("cx", xCoord).attr("cy", yTY).style("opacity", 1);
				focusDotLY.attr("cx", xCoord).attr("cy", yLY).style("opacity", 1);

				tooltip
					.style("opacity", 1)
					.html(
						`
					<strong>${d[x.dataCol] ?? 0}</strong><br/>
					TY SALES: ${d[l2.dataCol] ?? 0}<br/>
					LY SALES: ${d[l1.dataCol] ?? 0}<br/>
					TY ASP: ${d.tyASP ?? 0}<br/>
					LY ASP: ${d.lyASP ?? 0}<br/>
					SALES G/D: ${d.salesGD ?? 0}%<br/>
					ASP G/DG: ${d.aspGDG ?? 0}%
			   `
					)
					.style("left", `${event.pageX - 50}px`)
					.style("top", `${event.pageY - 100}px`);
			})
			.on("mouseout", () => {
				focusLine.style("opacity", 0);
				focusDotTY.style("opacity", 0);
				focusDotLY.style("opacity", 0);
				tooltip.style("opacity", 0);
			});
		const legend = chart.append("g").attr("transform", "translate(50, 15)"); // above chart

		legend.append("circle").attr("cx", 0).attr("cy", 0).attr("r", 5).style("fill", l2.color);

		legend
			.append("text")
			.attr("x", 6)
			.attr("y", 4)
			.text("TY")
			.style("font-size", "10px")
			.style("font-weight", "bold")
			.style("fill", "#333");

		legend.append("circle").attr("cx", 35).attr("cy", 0).attr("r", 5).style("fill", l1.color);

		legend
			.append("text")
			.attr("x", 41)
			.attr("y", 4)
			.text("LY")
			.style("font-size", "10px")
			.style("font-weight", "bold")
			.style("fill", "#333");

		data.forEach((d) => {
			let l1val = isNaN(d[l1.dataCol]) ? 0 : +d[l1.dataCol];
			let l2val = isNaN(d[l2.dataCol]) ? 0 : +d[l2.dataCol];

			const l1valDisplay = typeof l1?.getColVal === "function" ? l1.getColVal(l1val) : l1val;
			const l2valDisplay = typeof l2?.getColVal === "function" ? l2.getColVal(l2val) : l2val;
			// TY SALES value label (hotpink)
			if (d[x.dataCol]) {
				if (l2val)
					chart
						.append("text")
						.attr("x", xScale(d[x.dataCol]) - 10)
						.attr("y", yScale(l2val) - 8) // slightly above the point
						.attr("text-anchor", "middle")
						.style("font-size", "11px")
						.style("fill", "#000")
						.text(l2valDisplay); // format as needed

				// LY SALES value label (purple)
				if (l1val)
					chart
						.append("text")
						.attr("x", xScale(d[x.dataCol]))
						.attr("y", yScale(l1val) + 14) // slightly below the point
						.attr("text-anchor", "middle")
						.style("font-size", "11px")
						.style("fill", "#000")
						.text(l1valDisplay);
			}
		});
	}, [data]);

	return (
		<Box sx={{ width: "100%" }}>
			<svg ref={svgRef} />
			<div ref={tooltipRef} className="d3_2_line_graph_tooltip" />
		</Box>
	);
};

export default AreaGraphTwoLine;
